import { useState, useEffect } from "react";
import { Moon, Sun, Bell, Globe, Search, Menu } from "lucide-react";
import { Avatar } from "antd";
import Logo from "../../../src/assets/image.png";
import jope from "../../../src/assets/jope.png";
import Mightier from "../../../src/assets/Mightier_logo.png";
import axiosInstance from "../../utils/axiosInstance";
import { useNavigate } from "react-router-dom";
import { getTenantFromToken } from "../../utils/utils";
import UaAdsLogo from "../../../src/assets/universal-ads.svg";
import efileLogo from "../../../src/assets/efile.png";
import BrandedLoginHeader from "./BrandedLoginHeader";

interface SiteHeaderProps {
  progress?: number;
  onTrackProgress?: () => void;
}

const clientLogos: Record<string, string> = {
  "7563c07c-2572-4cc2-b14b-1d43b6010a78": jope,
  "00000000-0000-0000-0000-000000000000": Logo,
  "544189b1-c029-4dd6-b651-15207fccfe76": Mightier,
  "2e332294-8271-4a0e-8391-8b635f86a27e": UaAdsLogo,
  "31bb6772-77b5-4142-bcfc-04ff26927b54": efileLogo,
};

// Tenants that use a custom branded text header instead of a logo image
const customBrandedClients: Record<
  string,
  { companyName: string; accentColor?: string }
> = {
  // TODO: replace with NBCUniversal's actual tenant UUID
  "2bccf661-3b86-4839-884b-921268d0a94e": {
    companyName: "NBCUniversal",
    accentColor: "#1D2D82",
  },
};
const SiteHeader = ({ progress = 75, onTrackProgress }: SiteHeaderProps) => {
  const [darkMode, setDarkMode] = useState(false);
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const handleLogout = async () => {
    await axiosInstance.get("/api/logout");
    navigate("/auth/login?logout=true");
  };

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [darkMode]);

  const getClientLogo = (clientId: string): string | undefined => {
    return clientLogos[clientId];
  };
  const tenant = getTenantFromToken() ?? "";
  const logo = getClientLogo(tenant);
  const customBranding = customBrandedClients[tenant];
  return (
    <header className="relative">
      {/* Main Header */}
      <div className="flex flex-col lg:flex-row p-4 border-b bg-white dark:bg-gray-900 dark:border-gray-700">
        <div className="flex items-center w-full lg:w-[240px] lg:flex-shrink-0">
          {/* Mobile Menu Button - Only visible on mobile */}
          <button
            className="lg:hidden mr-4"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <Menu className="w-6 h-6 text-gray-600 dark:text-gray-300" />
          </button>

          {customBranding ? (
            <div className="ml-10">
              <BrandedLoginHeader
                companyName={customBranding.companyName}
                accentColor={customBranding.accentColor}
              />
            </div>
          ) : (
            <img className="h-10 w-auto ml-10" src={logo} />
          )}

          {/* Mobile Header Icons - Only visible on mobile */}
          <div className="flex lg:hidden items-center gap-2 ml-auto">
            {/* <img
              src="./country.png"
              alt="US"
              className="rounded-full w-6 h-6"
            /> */}
            <button onClick={() => setDarkMode(!darkMode)}>
              {darkMode ? (
                <Sun className="w-5 h-5 text-yellow-500" />
              ) : (
                <Moon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              )}
            </button>
          </div>
        </div>

        {/* Right Side Icons - Desktop */}
        <div className="hidden lg:flex items-center gap-4 ml-auto">
          {/* <div className="flex items-center rounded-full">
            <img
              src="./country.png"
              alt="US"
              className="rounded-full w-6 h-6 mr-2"
            />
          </div> */}

          <Bell className="w-5 h-5 text-gray-600 dark:text-gray-300 cursor-pointer" />
          <button onClick={() => setDarkMode(!darkMode)}>
            {darkMode ? (
              <Sun className="w-5 h-5 text-yellow-500" />
            ) : (
              <Moon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            )}
          </button>
          <button
            onClick={handleLogout}
            className="text-sm text-red-600 dark:text-red-400"
          >
            Logout
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 right-0 bg-white dark:bg-gray-900 border-b dark:border-gray-700 p-4 z-50">
          <div className="flex justify-around">
            <Bell className="w-5 h-5 text-gray-600 dark:text-gray-300 cursor-pointer" />
          </div>
        </div>
      )}
    </header>
  );
};

export default SiteHeader;